import type { ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { supportsWebGL } from '@/lib/webgl'
import { prefersReducedMotion } from '@/lib/preferences'
import { bodyByKey } from '@/constants/solar-system'

interface PlanetGlobeProps {
    bodyKey: string
    className?: string
}

/** A small rotating globe of one world, lit from the side. */
export const PlanetGlobe = ({ bodyKey, className }: PlanetGlobeProps): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)

    useEffect(() => {
        const canvas = canvasRef.current

        // Without WebGL the globe simply stays empty; the facts beside it still read.
        if (!canvas || !supportsWebGL()) return

        let disposed = false
        let frame = 0
        let cleanup = () => undefined as void

        const start = async () => {
            const THREE = await import('three')
            const { loadTexture } = await import('@/lib/three/textures')

            if (disposed) return

            const body = bodyByKey(bodyKey)
            const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true })
            const size = Math.max(64, canvas.clientWidth)

            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
            renderer.setSize(size, size, false)

            const scene = new THREE.Scene()
            const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 100)

            camera.position.set(0, bodyKey === 'saturn' ? 1.4 : 0, bodyKey === 'saturn' ? 9 : 4.6)
            camera.lookAt(0, 0, 0)

            const light = new THREE.DirectionalLight(new THREE.Color('#fff4e6'), 2.8)

            light.position.set(3, 1.2, 2.2)
            scene.add(light)
            scene.add(new THREE.AmbientLight(new THREE.Color('#2a3348'), 0.12))

            const isSun = bodyKey === 'sun'
            const material = isSun
                ? new THREE.MeshBasicMaterial({ color: new THREE.Color('#ffcf7a') })
                : new THREE.MeshStandardMaterial({ color: new THREE.Color(body?.colour ?? '#999999'), roughness: 1, metalness: 0 })
            const globe = new THREE.Mesh(new THREE.SphereGeometry(1, 64, 48), material)
            const holder = new THREE.Group()

            holder.add(globe)
            holder.rotation.z = bodyKey === 'uranus' ? 1.7 : bodyKey === 'saturn' ? 0.45 : 0.4
            scene.add(holder)

            if (body?.texture) {
                loadTexture(body.texture).then(texture => {
                    material.map = texture
                    material.color.set('#ffffff')
                    material.needsUpdate = true
                }).catch(() => undefined)
            }

            if (bodyKey === 'saturn') {
                const ring = new THREE.RingGeometry(1.24, 2.35, 128, 1)
                const uv = ring.attributes.uv
                const position = ring.attributes.position

                for (let i = 0; i < position.count; i++) {
                    uv.setXY(i, (Math.hypot(position.getX(i), position.getY(i)) - 1.24) / (2.35 - 1.24), 0.5)
                }

                const ringMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color('#d8c8a4'), side: THREE.DoubleSide, transparent: true, opacity: 0.9, roughness: 1, depthWrite: false })
                const ringMesh = new THREE.Mesh(ring, ringMaterial)

                ringMesh.rotation.x = -Math.PI / 2
                holder.add(ringMesh)

                loadTexture('/textures/2k_saturn_ring_alpha.png').then(texture => {
                    ringMaterial.map = texture
                    ringMaterial.color.set('#ffffff')
                    ringMaterial.needsUpdate = true
                }).catch(() => undefined)
            }

            const render = () => {
                if (!prefersReducedMotion()) globe.rotation.y += isSun ? 0.0015 : 0.004
                renderer.render(scene, camera)

                if (!disposed) frame = requestAnimationFrame(render)
            }

            frame = requestAnimationFrame(render)

            cleanup = () => {
                cancelAnimationFrame(frame)
                scene.traverse(object => {
                    const mesh = object as InstanceType<typeof THREE.Mesh>

                    mesh.geometry?.dispose()

                    if (mesh.material && !Array.isArray(mesh.material)) mesh.material.dispose()
                })
                renderer.dispose()

                // The card closed: release the GL context so reopening it never exhausts the browser's limit.
                if (!canvas.isConnected) renderer.forceContextLoss()
            }

            if (disposed) cleanup()
        }

        // Without WebGL the globe simply stays empty; the facts beside it still read.
        start().catch(() => undefined)

        return () => {
            disposed = true
            cleanup()
        }
    }, [bodyKey])

    return (
        <span className={cn('media relative block aspect-square w-full', className)}>
            <canvas ref={canvasRef} role='img' aria-label={`${bodyByKey(bodyKey)?.name ?? 'Planet'} turning slowly`} className='block h-full w-full' />
        </span>
    )
}
